import { AutopilotEventStatusEnum } from "@clockwise/schema";
import { FlexDetailsInput } from "@clockwise/schema/v2";
import { DateTime } from "luxon";
import React, { useEffect } from "react";
import { EventAttendeesProvider } from "../../event-card/hooks/EventAttendeesContext";
import {
  EventFlexConstraintsProvider,
  useEventFlexConstraints,
} from "../../event-card/hooks/EventFlexConstraintsContext";
import { ECFlexSettings } from "../../event-card/molecules/ECFlexSettings";
import { useUserProfile } from "../../hooks/useUserProfile";
import { useSetFlexibility } from "../useSetFlexibility";

export type EventFlexMeta = {
  eventId: string;
  startTime: DateTime;
  flexDetails: FlexDetailsInput;
};

const FlexSettingsModal = ({
  isOpen,
  eventId,
  closeFlexModal,
  flexDetails = { isFlexible: false },
}: {
  isOpen: boolean;
  eventId: string;
  closeFlexModal: () => void;
  flexDetails?: FlexDetailsInput;
}) => {
  const { handleFlexibilityChange } = useSetFlexibility([], "Saving flex details");
  const {
    flexStatus,
    meetingHoursConflict,
    availableWorkDays,
    minMaxRecommendedValues,
    usersWorkingHours,
  } = useEventFlexConstraints();

  const saveFlexDetails = (flexDetailsNew: FlexDetailsInput) => {
    handleFlexibilityChange(
      eventId,
      flexDetailsNew.isFlexible,
      flexDetailsNew?.flexRange || undefined,
      flexDetailsNew?.timeOfDayRange || undefined,
      flexDetailsNew?.allowedDays || [],
    );
    closeFlexModal();
  };

  return (
    <ECFlexSettings
      isOpen={isOpen}
      goBack={closeFlexModal}
      flexDetails={flexDetails}
      onFlexDetailsChange={saveFlexDetails}
      availableWorkDays={availableWorkDays}
      meetingHoursConflict={meetingHoursConflict}
      minMaxRecommendedValues={minMaxRecommendedValues}
      usersWorkingHours={usersWorkingHours}
      flexStatus={flexStatus ?? AutopilotEventStatusEnum.CanMove}
    />
  );
};

export const FlexSettingsModalWrapper = ({
  eventFlexMeta,
  removeEventId,
}: {
  eventFlexMeta: EventFlexMeta | null;
  removeEventId: () => void;
}) => {
  const { userProfile } = useUserProfile();
  const [isOpen, setIsOpen] = React.useState(false);

  useEffect(() => {
    if (eventFlexMeta?.eventId && !isOpen) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [eventFlexMeta?.eventId]);

  return (
    // SAM TODO: Add attendees (or make the constraints hook better)
    <EventAttendeesProvider attendees={[]}>
      <EventFlexConstraintsProvider
        startTime={eventFlexMeta?.startTime || DateTime.now()}
        calendarId={userProfile.primaryCalendar || ""}
        eventId={eventFlexMeta?.eventId || ""}
      >
        <FlexSettingsModal
          isOpen={isOpen}
          eventId={eventFlexMeta?.eventId || ""}
          flexDetails={eventFlexMeta?.flexDetails}
          closeFlexModal={() => {
            setIsOpen(false);
            removeEventId();
          }}
        />
      </EventFlexConstraintsProvider>
    </EventAttendeesProvider>
  );
};
